
import { JavaModule } from './types';

export const ENHANCED_JAVA_BASIC_MODULES: JavaModule[] = [
  {
    id: 'java-introduction',
    title: 'Introduction to Java',
    description: 'Learn the fundamentals of Java programming language and its ecosystem.',
    content: `
# Introduction to Java Programming

Java is a powerful, object-oriented programming language that has been a cornerstone of enterprise software development for decades.

## What is Java?

Java is a high-level, class-based, object-oriented programming language designed to have as few implementation dependencies as possible. It is a general-purpose programming language intended to let application developers write once, run anywhere (WORA).

## Key Features of Java

### 1. Platform Independence
- **Write Once, Run Anywhere (WORA)**: Java code compiles to bytecode that runs on the Java Virtual Machine (JVM)
- Cross-platform compatibility across different operating systems

### 2. Object-Oriented Programming
- Everything in Java is an object (except primitives)
- Supports key OOP principles: Encapsulation, Inheritance, Polymorphism, and Abstraction

### 3. Memory Management
- Automatic garbage collection manages memory allocation and deallocation
- Reduces memory leaks and programming errors

## Java Development Environment

### Installing Java Development Kit (JDK)
1. Download JDK from Oracle or OpenJDK
2. Set JAVA_HOME environment variable
3. Add Java bin directory to PATH

### Your First Java Program

\`\`\`java
public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
    }
}
\`\`\`

### Compilation and Execution
\`\`\`bash
# Compile Java source code
javac HelloWorld.java

# Run the compiled bytecode
java HelloWorld
\`\`\`

## Java Application Types

- **Desktop Applications**: Using Swing or JavaFX
- **Web Applications**: Using Spring, Struts, or JSF
- **Enterprise Applications**: Using Java EE/Jakarta EE
- **Mobile Applications**: Android development
- **Microservices**: Using Spring Boot

## Why Learn Java?

1. **Industry Demand**: High demand for Java developers
2. **Stability**: Mature language with strong backward compatibility
3. **Community**: Large, active developer community
4. **Ecosystem**: Rich ecosystem of frameworks and libraries
5. **Career Growth**: Excellent career prospects and salary potential
    `,
    duration: '45 minutes',
    difficulty: 'beginner',
    order: 1,
    estimatedTime: '45 minutes',
    objectives: [
      'Understand what Java is and its key features',
      'Set up Java development environment',
      'Write and run your first Java program',
      'Understand Java compilation process'
    ],
    learningObjectives: [
      'Understand what Java is and its key features',
      'Set up Java development environment',
      'Write and run your first Java program',
      'Understand Java compilation process'
    ],
    prerequisites: [],
    codeExamples: [
      {
        title: 'Hello World Program',
        code: `public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
    }
}`,
        explanation: 'This is the simplest Java program that prints "Hello, World!" to the console.'
      }
    ],
    quiz: {
      questions: [
        {
          id: 'java-intro-1',
          question: 'What does WORA stand for in Java?',
          options: [
            'Write Once, Read Anywhere',
            'Write Once, Run Anywhere',
            'Work Once, Run Always',
            'Write Only, Run Anywhere'
          ],
          correctAnswer: 1,
          explanation: 'WORA stands for "Write Once, Run Anywhere", which means Java code can run on any platform that has a JVM.',
          difficulty: 'easy',
          category: 'fundamentals',
          points: 10
        },
        {
          id: 'java-intro-2',
          question: 'Which component is responsible for executing Java bytecode?',
          options: [
            'Java Compiler (javac)',
            'Java Virtual Machine (JVM)',
            'Java Development Kit (JDK)',
            'Java Runtime Environment (JRE)'
          ],
          correctAnswer: 1,
          explanation: 'The Java Virtual Machine (JVM) is responsible for executing Java bytecode on the target platform.',
          difficulty: 'medium',
          category: 'fundamentals',
          points: 15
        }
      ]
    }
  }
];
